/**
 * Minimal QR Code encoder (ISO/IEC 18004) — byte mode only.
 * Supports versions 1–40, error correction levels L/M/Q/H.
 * No runtime dependencies.
 */

export type ErrorCorrectionLevel = "L" | "M" | "Q" | "H";

export type QrCodeOptions = {
  /** ECC level. Default M (≈15%). */
  ecc?: ErrorCorrectionLevel;
  /** Quiet zone modules (default 4). */
  margin?: number;
};

const ECC_INDEX: Record<ErrorCorrectionLevel, number> = {
  L: 0,
  M: 1,
  Q: 2,
  H: 3,
};

// [version-1][ecc] = [ecCodewordsPerBlock, numBlocksG1, dataCwG1, numBlocksG2, dataCwG2]
type EcBlock = readonly [number, number, number, number, number];

const EC_BLOCKS: readonly (readonly EcBlock[])[] = [
  [
    [7, 1, 19, 0, 0],
    [10, 1, 16, 0, 0],
    [13, 1, 13, 0, 0],
    [17, 1, 9, 0, 0],
  ],
  [
    [10, 1, 34, 0, 0],
    [16, 1, 28, 0, 0],
    [22, 1, 22, 0, 0],
    [28, 1, 16, 0, 0],
  ],
  [
    [15, 1, 55, 0, 0],
    [26, 1, 44, 0, 0],
    [18, 2, 17, 0, 0],
    [22, 2, 13, 0, 0],
  ],
  [
    [20, 1, 80, 0, 0],
    [18, 2, 32, 0, 0],
    [26, 2, 24, 0, 0],
    [16, 4, 9, 0, 0],
  ],
  [
    [26, 1, 108, 0, 0],
    [24, 2, 43, 0, 0],
    [18, 2, 15, 2, 16],
    [22, 2, 11, 2, 12],
  ],
  [
    [18, 2, 68, 0, 0],
    [16, 4, 27, 0, 0],
    [24, 4, 19, 0, 0],
    [28, 4, 15, 0, 0],
  ],
  [
    [20, 2, 78, 0, 0],
    [18, 4, 31, 0, 0],
    [18, 2, 14, 4, 15],
    [26, 4, 13, 1, 14],
  ],
  [
    [24, 2, 97, 0, 0],
    [22, 2, 38, 2, 39],
    [22, 4, 18, 2, 19],
    [26, 4, 14, 2, 15],
  ],
  [
    [30, 2, 116, 0, 0],
    [22, 3, 36, 2, 37],
    [20, 4, 16, 4, 17],
    [24, 4, 12, 4, 13],
  ],
  [
    [18, 2, 68, 2, 69],
    [26, 4, 43, 1, 44],
    [24, 6, 19, 2, 20],
    [28, 6, 15, 2, 16],
  ],
  [
    [20, 4, 81, 0, 0],
    [30, 1, 50, 4, 51],
    [28, 4, 22, 4, 23],
    [24, 3, 12, 8, 13],
  ],
  [
    [24, 2, 92, 2, 93],
    [22, 6, 36, 2, 37],
    [26, 4, 20, 6, 21],
    [28, 7, 14, 4, 15],
  ],
  [
    [26, 4, 107, 0, 0],
    [22, 8, 37, 1, 38],
    [24, 8, 20, 4, 21],
    [22, 12, 11, 4, 12],
  ],
  [
    [30, 3, 115, 1, 116],
    [24, 4, 40, 5, 41],
    [20, 11, 16, 5, 17],
    [24, 11, 12, 5, 13],
  ],
  [
    [22, 5, 87, 1, 88],
    [24, 5, 41, 5, 42],
    [30, 5, 24, 7, 25],
    [24, 11, 12, 7, 13],
  ],
  [
    [24, 5, 98, 1, 99],
    [28, 7, 45, 3, 46],
    [24, 15, 19, 2, 20],
    [30, 3, 15, 13, 16],
  ],
  [
    [28, 1, 107, 5, 108],
    [28, 10, 46, 1, 47],
    [28, 1, 22, 15, 23],
    [28, 2, 14, 17, 15],
  ],
  [
    [30, 5, 120, 1, 121],
    [26, 9, 43, 4, 44],
    [28, 17, 22, 1, 23],
    [28, 2, 14, 19, 15],
  ],
  [
    [28, 3, 113, 4, 114],
    [26, 3, 44, 11, 45],
    [26, 17, 21, 4, 22],
    [26, 9, 13, 16, 14],
  ],
  [
    [28, 3, 107, 5, 108],
    [26, 3, 41, 13, 42],
    [30, 15, 24, 5, 25],
    [28, 15, 15, 10, 16],
  ],
  [
    [28, 4, 116, 4, 117],
    [26, 17, 42, 0, 0],
    [28, 17, 22, 6, 23],
    [30, 19, 16, 6, 17],
  ],
  [
    [28, 2, 111, 7, 112],
    [28, 17, 46, 0, 0],
    [30, 7, 24, 16, 25],
    [24, 34, 13, 0, 0],
  ],
  [
    [30, 4, 121, 5, 122],
    [28, 4, 47, 14, 48],
    [30, 11, 24, 14, 25],
    [30, 16, 15, 14, 16],
  ],
  [
    [30, 6, 117, 4, 118],
    [28, 6, 45, 14, 46],
    [30, 11, 24, 16, 25],
    [30, 30, 16, 2, 17],
  ],
  [
    [26, 8, 106, 4, 107],
    [28, 8, 47, 13, 48],
    [30, 7, 24, 22, 25],
    [30, 22, 15, 13, 16],
  ],
  [
    [28, 10, 114, 2, 115],
    [28, 19, 46, 4, 47],
    [28, 28, 22, 6, 23],
    [30, 33, 16, 4, 17],
  ],
  [
    [30, 8, 122, 4, 123],
    [28, 22, 45, 3, 46],
    [30, 8, 23, 26, 24],
    [30, 12, 15, 28, 16],
  ],
  [
    [30, 3, 117, 10, 118],
    [28, 3, 45, 23, 46],
    [30, 4, 24, 31, 25],
    [30, 11, 15, 31, 16],
  ],
  [
    [30, 7, 116, 7, 117],
    [28, 21, 45, 7, 46],
    [30, 1, 23, 37, 24],
    [30, 19, 15, 26, 16],
  ],
  [
    [30, 5, 115, 10, 116],
    [28, 19, 47, 10, 48],
    [30, 15, 24, 25, 25],
    [30, 23, 15, 25, 16],
  ],
  [
    [30, 13, 115, 3, 116],
    [28, 2, 46, 29, 47],
    [30, 42, 24, 1, 25],
    [30, 23, 15, 28, 16],
  ],
  [
    [30, 17, 115, 0, 0],
    [28, 10, 46, 23, 47],
    [30, 10, 24, 35, 25],
    [30, 19, 15, 35, 16],
  ],
  [
    [30, 17, 115, 1, 116],
    [28, 14, 46, 21, 47],
    [30, 29, 24, 19, 25],
    [30, 11, 15, 46, 16],
  ],
  [
    [30, 13, 115, 6, 116],
    [28, 14, 46, 23, 47],
    [30, 44, 24, 7, 25],
    [30, 59, 16, 1, 17],
  ],
  [
    [30, 12, 121, 7, 122],
    [28, 12, 47, 26, 48],
    [30, 39, 24, 14, 25],
    [30, 22, 15, 41, 16],
  ],
  [
    [30, 6, 121, 14, 122],
    [28, 6, 47, 34, 48],
    [30, 46, 24, 10, 25],
    [30, 2, 15, 64, 16],
  ],
  [
    [30, 17, 122, 4, 123],
    [28, 29, 46, 14, 47],
    [30, 49, 24, 10, 25],
    [30, 24, 15, 46, 16],
  ],
  [
    [30, 4, 122, 18, 123],
    [28, 13, 46, 32, 47],
    [30, 48, 24, 14, 25],
    [30, 42, 15, 32, 16],
  ],
  [
    [30, 20, 117, 4, 118],
    [28, 40, 47, 7, 48],
    [30, 43, 24, 22, 25],
    [30, 10, 15, 67, 16],
  ],
  [
    [30, 19, 118, 6, 119],
    [28, 18, 47, 31, 48],
    [30, 34, 24, 34, 25],
    [30, 20, 15, 61, 16],
  ],
];

const ALIGNMENT_CENTERS: readonly (readonly number[])[] = [
  [],
  [6, 18],
  [6, 22],
  [6, 26],
  [6, 30],
  [6, 34],
  [6, 22, 38],
  [6, 24, 42],
  [6, 26, 46],
  [6, 28, 50],
  [6, 30, 54],
  [6, 32, 58],
  [6, 34, 62],
  [6, 26, 46, 66],
  [6, 26, 48, 70],
  [6, 26, 50, 74],
  [6, 30, 54, 78],
  [6, 30, 56, 82],
  [6, 30, 58, 86],
  [6, 34, 62, 90],
  [6, 28, 50, 72, 94],
  [6, 26, 50, 74, 98],
  [6, 30, 54, 78, 102],
  [6, 28, 54, 80, 106],
  [6, 32, 58, 84, 110],
  [6, 30, 58, 86, 114],
  [6, 34, 62, 90, 118],
  [6, 26, 50, 74, 98, 122],
  [6, 30, 54, 78, 102, 126],
  [6, 26, 52, 78, 104, 130],
  [6, 30, 56, 82, 108, 134],
  [6, 34, 60, 86, 112, 138],
  [6, 30, 58, 86, 114, 142],
  [6, 34, 62, 90, 118, 146],
  [6, 30, 54, 78, 102, 126, 150],
  [6, 24, 50, 76, 102, 128, 154],
  [6, 28, 54, 80, 106, 132, 158],
  [6, 32, 58, 84, 110, 136, 162],
  [6, 26, 54, 82, 110, 138, 166],
  [6, 30, 58, 86, 114, 142, 170],
];

/** Format info BCH codes indexed by [ecc][mask]. */
const FORMAT_INFO: readonly (readonly number[])[] = [
  [0x77c4, 0x72f3, 0x7daa, 0x789d, 0x662f, 0x6318, 0x6c41, 0x6976],
  [0x5412, 0x5125, 0x5e7c, 0x5b4b, 0x45f9, 0x40ce, 0x4f97, 0x4aa0],
  [0x355f, 0x3068, 0x3f31, 0x3a06, 0x24b4, 0x2183, 0x2eda, 0x2bed],
  [0x1689, 0x13be, 0x1ce7, 0x19d0, 0x0762, 0x0255, 0x0d0c, 0x083b],
];

const VERSION_INFO = [
  0x07c94, 0x085bc, 0x09a99, 0x0a4d3, 0x0bbf6, 0x0c762, 0x0d847, 0x0e60d, 0x0f928,
  0x10b78, 0x1145d, 0x12a17, 0x13532, 0x149a6, 0x15683, 0x168c9, 0x177ec, 0x18ec4,
  0x191e1, 0x1afab, 0x1b08e, 0x1cc1a, 0x1d33f, 0x1ed75, 0x1f250, 0x209d5, 0x216f0,
  0x228ba, 0x2379f, 0x24b0b, 0x2542e, 0x26a64, 0x27541, 0x28c69,
] as const;

// —— Spec table access ——
// Each table is read through one accessor, so a bad version/ecc/mask fails loudly
// here instead of silently defaulting to zero at a dozen call sites.

function ecBlock(version: number, ecc: ErrorCorrectionLevel): EcBlock {
  const block = EC_BLOCKS[version - 1]?.[ECC_INDEX[ecc]];
  if (!block) {
    throw new Error(`No EC block table for version ${version} at level ${ecc}`);
  }
  return block;
}

function alignmentCenters(version: number): readonly number[] {
  return ALIGNMENT_CENTERS[version - 1] ?? [];
}

function formatBits(ecc: ErrorCorrectionLevel, mask: number): number {
  const bits = FORMAT_INFO[ECC_INDEX[ecc]]?.[mask];
  if (bits === undefined) {
    throw new Error(`No format info for level ${ecc} mask ${mask}`);
  }
  return bits;
}

function versionBits(version: number): number {
  const bits = VERSION_INFO[version - 7];
  if (bits === undefined) {
    throw new Error(`No version info for version ${version}`);
  }
  return bits;
}

// —— GF(256) Reed–Solomon ——

const GF_EXP = new Uint8Array(512);
const GF_LOG = new Uint8Array(256);

(() => {
  let x = 1;
  for (let i = 0; i < 255; i++) {
    GF_EXP[i] = x;
    GF_LOG[x] = i;
    x <<= 1;
    if (x >= 256) {
      x ^= 0x11d;
    }
  }
  for (let i = 255; i < 512; i++) {
    GF_EXP[i] = GF_EXP[i - 255] ?? 0;
  }
})();

/** Byte read with an in-range default, so callers stay free of index guards. */
function byteAt(bytes: Uint8Array, index: number): number {
  return bytes[index] ?? 0;
}

function gfMul(a: number, b: number): number {
  if (a === 0 || b === 0) {
    return 0;
  }
  return byteAt(GF_EXP, byteAt(GF_LOG, a) + byteAt(GF_LOG, b));
}

/** Generator polynomial for `ecCount` codewords, highest degree first. */
function generatorPoly(ecCount: number): Uint8Array {
  let gen = Uint8Array.of(1);
  for (let i = 0; i < ecCount; i++) {
    const next = new Uint8Array(gen.length + 1);
    for (let j = 0; j < gen.length; j++) {
      const coef = byteAt(gen, j);
      next[j] = byteAt(next, j) ^ coef; // poly × x
      next[j + 1] = byteAt(next, j + 1) ^ gfMul(coef, byteAt(GF_EXP, i)); // poly × α^i
    }
    gen = next;
  }
  return gen;
}

/** Reed–Solomon ECC codewords for one block. */
function rsEncode(data: Uint8Array, ecCount: number): Uint8Array {
  const gen = generatorPoly(ecCount);
  const result = new Uint8Array(ecCount);

  for (const byte of data) {
    const factor = byte ^ byteAt(result, 0);
    result.copyWithin(0, 1);
    result[ecCount - 1] = 0;
    if (factor === 0) {
      continue;
    }
    // gen = [1, g1, …, g_ec]; skip the leading 1
    for (let i = 1; i < gen.length; i++) {
      result[i - 1] = byteAt(result, i - 1) ^ gfMul(byteAt(gen, i), factor);
    }
  }
  return result;
}

// —— Bit packing ——

class BitBuffer {
  private readonly bits: number[] = [];

  get length(): number {
    return this.bits.length;
  }

  put(value: number, bitCount: number): void {
    for (let i = bitCount - 1; i >= 0; i--) {
      this.bits.push((value >>> i) & 1);
    }
  }

  toBytes(): Uint8Array {
    const out = new Uint8Array(Math.ceil(this.bits.length / 8));
    for (const [i, bit] of this.bits.entries()) {
      out[i >> 3] = byteAt(out, i >> 3) | (bit << (7 - (i & 7)));
    }
    return out;
  }
}

function charCountBits(version: number): number {
  return version < 10 ? 8 : 16;
}

function dataCapacityBytes(version: number, ecc: ErrorCorrectionLevel): number {
  const [, n1, d1, n2, d2] = ecBlock(version, ecc);
  return n1 * d1 + n2 * d2;
}

function selectVersion(dataLength: number, ecc: ErrorCorrectionLevel): number {
  for (let version = 1; version <= 40; version++) {
    const neededBits = 4 + charCountBits(version) + dataLength * 8 + 4;
    if (neededBits <= dataCapacityBytes(version, ecc) * 8) {
      return version;
    }
  }
  throw new Error("Data too long for QR Code (max version 40)");
}

function buildDataCodewords(
  data: Uint8Array,
  version: number,
  ecc: ErrorCorrectionLevel,
): Uint8Array {
  const capacity = dataCapacityBytes(version, ecc);
  const buf = new BitBuffer();
  buf.put(0b0100, 4); // byte mode
  buf.put(data.length, charCountBits(version));
  for (const byte of data) {
    buf.put(byte, 8);
  }
  buf.put(0, Math.min(4, capacity * 8 - buf.length)); // terminator
  while (buf.length % 8 !== 0) {
    buf.put(0, 1);
  }

  const bytes = [...buf.toBytes()];
  let pad = 0xec;
  while (bytes.length < capacity) {
    bytes.push(pad);
    pad = pad === 0xec ? 0x11 : 0xec;
  }
  return Uint8Array.from(bytes);
}

function interleaveCodewords(
  dataCodewords: Uint8Array,
  version: number,
  ecc: ErrorCorrectionLevel,
): Uint8Array {
  const [ecPerBlock, n1, d1, n2, d2] = ecBlock(version, ecc);

  const dataBlocks: Uint8Array[] = [];
  const ecBlocks: Uint8Array[] = [];
  let offset = 0;

  for (const [count, size] of [
    [n1, d1],
    [n2, d2],
  ] as const) {
    for (let i = 0; i < count; i++) {
      const block = dataCodewords.slice(offset, offset + size);
      offset += size;
      dataBlocks.push(block);
      ecBlocks.push(rsEncode(block, ecPerBlock));
    }
  }

  const result: number[] = [];
  for (let i = 0; i < Math.max(d1, d2); i++) {
    for (const block of dataBlocks) {
      if (i < block.length) {
        result.push(byteAt(block, i));
      }
    }
  }
  for (let i = 0; i < ecPerBlock; i++) {
    for (const block of ecBlocks) {
      result.push(byteAt(block, i));
    }
  }
  return Uint8Array.from(result);
}

// —— Module grid ——

/**
 * Square grid of modules: unset, light, or dark. Every read and write is bounds
 * checked here — once — so the placement algorithms below can address the grid
 * freely (patterns are drawn straddling the edges) without repeating the check.
 * Out-of-range reads report light/non-empty and out-of-range writes are dropped.
 */
class Grid {
  readonly size: number;
  /** -1 unset, 0 light, 1 dark. */
  private readonly cells: Int8Array;

  constructor(size: number) {
    this.size = size;
    this.cells = new Int8Array(size * size).fill(-1);
  }

  private inBounds(row: number, col: number): boolean {
    return row >= 0 && col >= 0 && row < this.size && col < this.size;
  }

  isDark(row: number, col: number): boolean {
    return this.inBounds(row, col) && this.cells[row * this.size + col] === 1;
  }

  isEmpty(row: number, col: number): boolean {
    return this.inBounds(row, col) && this.cells[row * this.size + col] === -1;
  }

  set(row: number, col: number, dark: boolean): void {
    if (this.inBounds(row, col)) {
      this.cells[row * this.size + col] = dark ? 1 : 0;
    }
  }

  setIfEmpty(row: number, col: number, dark: boolean): void {
    if (this.isEmpty(row, col)) {
      this.set(row, col, dark);
    }
  }

  flip(row: number, col: number): void {
    this.set(row, col, !this.isDark(row, col));
  }

  clone(): Grid {
    const copy = new Grid(this.size);
    copy.cells.set(this.cells);
    return copy;
  }
}

function sizeOf(version: number): number {
  return 17 + 4 * version;
}

/** Chebyshev distance from a centre — finders and alignments are concentric rings. */
function ring(dRow: number, dCol: number): number {
  return Math.max(Math.abs(dRow), Math.abs(dCol));
}

/** 7×7 finder (dark ring 3, light ring 2, dark 3×3 core) plus its light separator. */
function placeFinder(grid: Grid, top: number, left: number): void {
  for (let r = -1; r <= 7; r++) {
    for (let c = -1; c <= 7; c++) {
      const distance = ring(r - 3, c - 3);
      grid.set(top + r, left + c, distance <= 3 && distance !== 2);
    }
  }
}

/** 5×5 alignment patterns (dark ring 2, light ring 1, dark centre). */
function placeAlignments(grid: Grid, version: number): void {
  const centers = alignmentCenters(version);
  for (const row of centers) {
    for (const col of centers) {
      if (!grid.isEmpty(row, col)) {
        continue; // centre collides with a finder
      }
      for (let dr = -2; dr <= 2; dr++) {
        for (let dc = -2; dc <= 2; dc++) {
          grid.set(row + dr, col + dc, ring(dr, dc) !== 1);
        }
      }
    }
  }
}

function placeTiming(grid: Grid): void {
  for (let i = 8; i < grid.size - 8; i++) {
    const dark = i % 2 === 0;
    grid.setIfEmpty(6, i, dark);
    grid.setIfEmpty(i, 6, dark);
  }
}

/** Claim the format/version areas so data placement and masking skip them. */
function reserveFormat(grid: Grid): void {
  const n = grid.size;
  for (let i = 0; i < 9; i++) {
    grid.setIfEmpty(8, i, false);
    grid.setIfEmpty(i, 8, false);
  }
  for (let i = 0; i < 8; i++) {
    grid.setIfEmpty(8, n - 1 - i, false);
  }
  for (let i = 0; i < 7; i++) {
    grid.setIfEmpty(n - 1 - i, 8, false);
  }
}

function reserveVersion(grid: Grid, version: number): void {
  if (version < 7) {
    return;
  }
  const n = grid.size;
  for (let i = 0; i < 6; i++) {
    for (let j = 0; j < 3; j++) {
      grid.set(i, n - 11 + j, false);
      grid.set(n - 11 + j, i, false);
    }
  }
}

function placeFunctionPatterns(grid: Grid, version: number): void {
  const n = grid.size;
  placeFinder(grid, 0, 0);
  placeFinder(grid, 0, n - 7);
  placeFinder(grid, n - 7, 0);
  placeAlignments(grid, version);
  placeTiming(grid);
  grid.set(4 * version + 9, 8, true); // always-dark module
  reserveFormat(grid);
  reserveVersion(grid, version);
}

/** The 15 format-bit cells around the top-left finder, least significant first. */
const FORMAT_POSITIONS: readonly (readonly [number, number])[] = [
  [8, 0],
  [8, 1],
  [8, 2],
  [8, 3],
  [8, 4],
  [8, 5],
  [8, 7],
  [8, 8],
  [7, 8],
  [5, 8],
  [4, 8],
  [3, 8],
  [2, 8],
  [1, 8],
  [0, 8],
];

function placeFormatBits(grid: Grid, bits: number): void {
  const n = grid.size;
  for (const [i, [row, col]] of FORMAT_POSITIONS.entries()) {
    grid.set(row, col, ((bits >> i) & 1) === 1);
  }
  // Second copy: top-right horizontal run and bottom-left vertical run.
  for (let i = 0; i < 8; i++) {
    grid.set(8, n - 1 - i, ((bits >> i) & 1) === 1);
  }
  for (let i = 0; i < 7; i++) {
    grid.set(n - 7 + i, 8, ((bits >> (i + 8)) & 1) === 1);
  }
}

function placeVersionBits(grid: Grid, bits: number): void {
  const n = grid.size;
  for (let i = 0; i < 18; i++) {
    const dark = ((bits >> i) & 1) === 1;
    const row = Math.floor(i / 3);
    const col = n - 11 + (i % 3);
    grid.set(row, col, dark);
    grid.set(col, row, dark);
  }
}

function remainderBitCount(version: number): number {
  if (version >= 2 && version <= 6) {
    return 7;
  }
  if (version >= 14 && version <= 20) {
    return 3;
  }
  if (version >= 21 && version <= 27) {
    return 4;
  }
  if (version >= 28 && version <= 34) {
    return 3;
  }
  return 0;
}

/** Zigzag the codeword bits up and down the two-module columns, right to left. */
function placeDataBits(grid: Grid, codewords: Uint8Array, version: number): void {
  const n = grid.size;
  const bits: number[] = [];
  for (const byte of codewords) {
    for (let i = 7; i >= 0; i--) {
      bits.push((byte >> i) & 1);
    }
  }
  for (let i = 0; i < remainderBitCount(version); i++) {
    bits.push(0);
  }

  let next = 0;
  let upward = true;
  for (let right = n - 1; right > 0; right -= 2) {
    if (right === 6) {
      right = 5; // skip the vertical timing pattern
    }
    for (let i = 0; i < n; i++) {
      const row = upward ? n - 1 - i : i;
      for (let dc = 0; dc < 2; dc++) {
        const col = right - dc;
        if (!grid.isEmpty(row, col)) {
          continue;
        }
        grid.set(row, col, bits[next] === 1);
        next++;
      }
    }
    upward = !upward;
  }
}

// —— Masking ——

function maskFn(mask: number, row: number, col: number): boolean {
  switch (mask) {
    case 0:
      return (row + col) % 2 === 0;
    case 1:
      return row % 2 === 0;
    case 2:
      return col % 3 === 0;
    case 3:
      return (row + col) % 3 === 0;
    case 4:
      return (Math.floor(row / 2) + Math.floor(col / 3)) % 2 === 0;
    case 5:
      return ((row * col) % 2) + ((row * col) % 3) === 0;
    case 6:
      return (((row * col) % 2) + ((row * col) % 3)) % 2 === 0;
    case 7:
      return (((row + col) % 2) + ((row * col) % 3)) % 2 === 0;
    default:
      return false;
  }
}

/**
 * Add format/version bits to a copy of `data` and mask its data modules.
 * `template` holds the function patterns alone, so anything still empty there is
 * a data module — the one place that distinction is recorded.
 */
function applyMask(
  data: Grid,
  template: Grid,
  version: number,
  ecc: ErrorCorrectionLevel,
  mask: number,
): Grid {
  const grid = data.clone();
  placeFormatBits(grid, formatBits(ecc, mask));
  if (version >= 7) {
    placeVersionBits(grid, versionBits(version));
  }
  for (let row = 0; row < grid.size; row++) {
    for (let col = 0; col < grid.size; col++) {
      if (template.isEmpty(row, col) && maskFn(mask, row, col)) {
        grid.flip(row, col);
      }
    }
  }
  return grid;
}

/** Finder-lookalike run (1:1:3:1:1 with four light modules) and its mirror. */
const FINDER_RUN = 0b00001011101;
const FINDER_RUN_MIRRORED = 0b10111010000;

/** N1 (runs of five or more) and N3 (finder lookalikes) for a single line. */
function linePenalty(length: number, isDark: (index: number) => boolean): number {
  let score = 0;
  let run = 0;
  let previous = false;
  let window = 0;

  for (let i = 0; i < length; i++) {
    const dark = isDark(i);
    run = i > 0 && dark === previous ? run + 1 : 1;
    previous = dark;
    if (run === 5) {
      score += 3;
    } else if (run > 5) {
      score += 1;
    }

    window = ((window << 1) | (dark ? 1 : 0)) & 0x7ff;
    if (i >= 10 && (window === FINDER_RUN || window === FINDER_RUN_MIRRORED)) {
      score += 40;
    }
  }
  return score;
}

/** Mask suitability score from the four ISO/IEC 18004 penalty rules; lower is better. */
function penalty(grid: Grid): number {
  const n = grid.size;
  let score = 0;

  // N1 + N3, over every row and every column.
  for (let i = 0; i < n; i++) {
    score += linePenalty(n, (k) => grid.isDark(i, k));
    score += linePenalty(n, (k) => grid.isDark(k, i));
  }

  // N2 — same-coloured 2×2 blocks.
  for (let r = 0; r < n - 1; r++) {
    for (let c = 0; c < n - 1; c++) {
      const dark = grid.isDark(r, c);
      if (
        dark === grid.isDark(r, c + 1) &&
        dark === grid.isDark(r + 1, c) &&
        dark === grid.isDark(r + 1, c + 1)
      ) {
        score += 3;
      }
    }
  }

  // N4 — deviation from an even balance of dark and light.
  let dark = 0;
  for (let r = 0; r < n; r++) {
    for (let c = 0; c < n; c++) {
      if (grid.isDark(r, c)) {
        dark++;
      }
    }
  }
  score += Math.floor(Math.abs((100 * dark) / (n * n) - 50) / 5) * 10;

  return score;
}

function chooseBestMask(
  data: Grid,
  template: Grid,
  version: number,
  ecc: ErrorCorrectionLevel,
): Grid {
  let best = applyMask(data, template, version, ecc, 0);
  let bestScore = penalty(best);
  for (let mask = 1; mask < 8; mask++) {
    const candidate = applyMask(data, template, version, ecc, mask);
    const score = penalty(candidate);
    if (score < bestScore) {
      bestScore = score;
      best = candidate;
    }
  }
  return best;
}

/** Copy `core` into a larger grid, leaving `margin` light modules all round. */
function withQuietZone(core: Grid, margin: number): Grid {
  const padded = new Grid(core.size + margin * 2);
  for (let row = 0; row < padded.size; row++) {
    for (let col = 0; col < padded.size; col++) {
      // Reads outside `core` report light, which is exactly the quiet zone.
      padded.set(row, col, core.isDark(row - margin, col - margin));
    }
  }
  return padded;
}

// —— Public API ——

/** An encoded QR symbol, quiet zone included. */
export class QrMatrix {
  /** QR version, 1–40. */
  readonly version: number;
  /** Quiet-zone modules on each side, already counted in `size`. */
  readonly margin: number;
  private readonly grid: Grid;

  constructor(version: number, margin: number, grid: Grid) {
    this.version = version;
    this.margin = margin;
    this.grid = grid;
  }

  /** Width and height in modules, quiet zone included. */
  get size(): number {
    return this.grid.size;
  }

  isDark(row: number, col: number): boolean {
    return this.grid.isDark(row, col);
  }
}

/** Encode UTF-8 text as a QR module matrix. */
export function encodeQr(text: string, options: QrCodeOptions = {}): QrMatrix {
  const ecc = options.ecc ?? "M";
  const margin = options.margin ?? 4;

  const bytes = new TextEncoder().encode(text);
  const version = selectVersion(bytes.length, ecc);
  const codewords = interleaveCodewords(
    buildDataCodewords(bytes, version, ecc),
    version,
    ecc,
  );

  // Function patterns are laid out once. The result is both the starting point for
  // data placement and the record of which modules are data (still empty).
  const template = new Grid(sizeOf(version));
  placeFunctionPatterns(template, version);

  const data = template.clone();
  placeDataBits(data, codewords, version);

  return new QrMatrix(
    version,
    margin,
    withQuietZone(chooseBestMask(data, template, version, ecc), margin),
  );
}

const SVG_NS = "http://www.w3.org/2000/svg";

/** SVG path data covering every dark module as a 1×1 square, in module units. */
export function qrToSvgPath(matrix: QrMatrix): string {
  const segments: string[] = [];
  for (let row = 0; row < matrix.size; row++) {
    for (let col = 0; col < matrix.size; col++) {
      if (matrix.isDark(row, col)) {
        segments.push(`M${col} ${row}h1v1h-1z`);
      }
    }
  }
  return segments.join("");
}

/**
 * Build an `<svg>` element for a QR matrix, sized by CSS. A QR code has to stay
 * dark-on-light to scan, so the modules are painted black on a transparent ground
 * and the (always light) frame behind the element supplies the contrast.
 */
export function qrToSvgElement(matrix: QrMatrix, label: string): SVGSVGElement {
  const svg = document.createElementNS(SVG_NS, "svg");
  svg.setAttribute("viewBox", `0 0 ${matrix.size} ${matrix.size}`);
  svg.setAttribute("shape-rendering", "crispEdges");
  svg.setAttribute("role", "img");
  svg.setAttribute("aria-label", label);

  const path = document.createElementNS(SVG_NS, "path");
  path.setAttribute("d", qrToSvgPath(matrix));
  path.setAttribute("fill", "#000");
  svg.append(path);

  return svg;
}
